"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Phone } from "lucide-react";
import Image from "next/image";
import logo from "../../../public/sel-logo.svg";

const helpLineIntro = "Besoin d'aide pour démarrer votre essai gratuit ?";
const helpLineAppelez = "Appelez-nous au";
const helpLinePrefix = `${helpLineIntro} ${helpLineAppelez}`;

/**
 * Mobile uniquement (barre `lg:hidden`) : texte sur plusieurs lignes + numéro dessous.
 * Ne pas réutiliser en desktop — voir DesktopHeaderHelpLine.
 */
function MobileStackedHelpLine() {
  return (
    <div
      lang="fr"
      className="flex min-w-0 flex-1 flex-col items-end gap-1.5 text-right sm:gap-2"
    >
      <p className="max-w-[min(100%,15.5rem)] text-pretty text-right text-xs font-normal leading-snug text-gray-800 min-[380px]:max-w-[18rem] min-[380px]:text-sm">
        <span className="text-gray-800">
          {helpLineIntro} {helpLineAppelez}
        </span>
      </p>
      <Link
        href="tel:+33187200200"
        className="inline-flex shrink-0 items-center justify-end gap-1.5 self-end rounded-lg py-0.5 font-semibold text-primaryGreen underline-offset-2 hover:underline"
      >
        <Phone className="h-5 w-5 shrink-0 text-primaryGreen" aria-hidden />
        <span className="whitespace-nowrap text-base font-bold tabular-nums tracking-tight min-[400px]:text-lg">
          01 87 200 200
        </span>
      </Link>
    </div>
  );
}

/**
 * Desktop (lg+) : même présentation sur toutes les pages — phrase + lien téléphone sur une ligne (wrap seulement si manque de place).
 */
function DesktopHeaderHelpLine() {
  return (
    <div lang="fr" className="min-w-0 flex-1 text-right">
      <p className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-sm leading-snug text-gray-800 xl:text-base">
        <span className="text-gray-800">{helpLinePrefix}</span>
        <Link
          href="tel:+33187200200"
          className="inline-flex shrink-0 items-center gap-2 font-semibold text-gray-900 whitespace-nowrap transition-colors hover:text-primaryGreen"
        >
          <Phone className="h-5 w-5 shrink-0 text-primaryGreen" aria-hidden />
          <span className="font-medium">01 87 200 200</span>
        </Link>
      </p>
    </div>
  );
}

/**
 * Mobile (sous breakpoint lg) : logo + aide téléphone empilée.
 * Desktop : logo + aide sur une ligne. Tunnel /commande : barre sticky.
 */
export const Header = () => {
  const pathname = usePathname() || "";
  const tunnel = pathname.startsWith("/commande");

  return (
    <div
      className={`w-full bg-white shadow-lg ${tunnel ? "sticky top-0 z-50" : "z-10"}`}
    >
      <div className="mx-auto flex max-w-screen-xl items-center justify-between gap-1.5 px-2 py-2 sm:gap-2 sm:px-4 sm:py-3 lg:hidden">
        <Link href="/" className="shrink-0">
          <Image
            src={logo}
            alt="Logo Standard en ligne"
            width={160}
            height={32}
            className="h-5 w-auto min-[380px]:h-6 sm:h-8"
            priority
          />
        </Link>
        <MobileStackedHelpLine />
      </div>
      <div className="mx-auto hidden max-w-screen-xl items-center justify-between gap-4 p-4 lg:flex xl:px-0">
        <Link href="/">
          <Image
            src={logo}
            alt="Logo"
            width={500}
            height={100}
            className="h-auto w-auto min-h-8"
          />
        </Link>
        <DesktopHeaderHelpLine />
      </div>
    </div>
  );
};
